import {
  activeAgent,
  apiLevel,
  formatClock,
  linkPhase,
  sameAgent,
  type AgentRef,
  type HealthLevel,
  type LinkPhase,
  type LinkSnapshot,
} from '@krp/client-runtime'

/**
 * Связь с агентом (п. 2.5 проекта EQW2). Чистые функции: фаза выводится из снимка и текущего времени, ничего не опрашивается.
 * Правило честности: «на связи» пишется только если агент действительно ответил.
 */

export type LinkActionId = 'recheck' | 'download-agent' | 'update-agent' | 'agent-pick' | 'copy-origin'
export type LinkGlyph = '…' | '✓' | '!' | '✕'

/**
 * Стадия диагностики связи с агентом — вид полосы и метки экрана «Оборудование» (рамка `hw-box--*`, `Mark`), а не уровень связи.
 * Уровень — общий словарь `HealthLevel` (RP-UI-LINK); стадия выводится из него одной таблицей `LINK_STAGE`.
 */
export type LinkStage = 'ok' | 'warn' | 'block' | 'wait'

export const LINK_STAGE: Readonly<Record<HealthLevel, LinkStage>> = { ok: 'ok', degraded: 'warn', down: 'block', unknown: 'wait' }

export interface AgentOptionVm {
  port: number
  version: string
  /** «служба», «запущена вручную» или `null`, если старый агент об этом не сообщает. */
  modeWord: string | null
  startedAt: string | null
  selected: boolean
}

export interface LinkVm {
  phase: LinkPhase
  /** Слово состояния (значок + слово: цвет не единственный признак). */
  word: string
  glyph: LinkGlyph
  /** Уровень связи с агентом — общий словарь приложения. */
  level: HealthLevel
  /** Вид полосы на экране по уровню (`LINK_STAGE`). */
  stage: LinkStage
  text: string
  /** Главное действие полосы; `null` — действия нет. */
  action: LinkActionId | null
  /** Данные показаны последние известные, не свежие: серые, с пометкой «на HH:MM». */
  stale: boolean
  staleNote: string | null
  /** Настройка выключена (связи нет, агент не пускает и т. п.). */
  actionsDisabled: boolean
  agent: AgentRef | null
  /** Предупреждение вне состояний: агент запущен не службой. */
  warning: string | null
  options: readonly AgentOptionVm[]
  /** Адрес сайта для `denied`. */
  siteOrigin: string | null
  /** Подсказки «Агент установлен, но не находится?». */
  troubleshooting: readonly string[]
}

/** Потеряна связь — `degraded`, а не `down`: на экране остаются последние известные данные (серые, «на HH:MM»). */
const WORDS: Record<LinkPhase, { word: string; glyph: LinkGlyph; level: HealthLevel }> = {
  searching: { word: 'Ищем', glyph: '…', level: 'unknown' },
  connected: { word: 'На связи', glyph: '✓', level: 'ok' },
  lost: { word: 'Связь потеряна', glyph: '!', level: 'degraded' },
  missing: { word: 'Не найден', glyph: '✕', level: 'down' },
  choose: { word: 'Найдено несколько', glyph: '!', level: 'degraded' },
  outdated: { word: 'Версия устарела', glyph: '!', level: 'degraded' },
  denied: { word: 'Агент не пускает этот сайт', glyph: '✕', level: 'down' },
}

export const MISSING_TROUBLESHOOTING: readonly string[] = [
  'Запущена ли служба «Kalmykov Cash Agent» (services.msc).',
  'Если браузер спросил про доступ к устройствам в локальной сети — нажмите «Разрешить».',
  'Используйте Chrome или Edge; Safari и Firefox могут не пускать https-страницу к 127.0.0.1.',
  'Возможно, адрес этого сайта не добавлен в настройки программы-драйвера.',
]

export const modeWord = (agent: AgentRef): string | null => {
  const mode = agent.hello.host?.mode ?? null
  return mode === 'service' ? 'служба' : mode === 'console' ? 'запущена вручную' : null
}

/** «Запущена в HH:MM» — общей утилитой времени связи (RP-UI-LINK). */
const startedClock = (agent: AgentRef): string | null => formatClock(agent.hello.host?.startedAt ?? null)

function describeAgent(agent: AgentRef): string {
  const parts = [`Программа-драйвер ${agent.hello.version}`, `порт ${agent.port}`]
  const mode = modeWord(agent)
  if (mode !== null) parts.push(mode)
  return parts.join(', ')
}

/** Полоса «Связь с агентом»: слово, значок, текст и действие по состоянию. */
export function linkState(link: LinkSnapshot, now: number): LinkVm {
  const phase = linkPhase(link, now)
  const agent = activeAgent(link)
  const base = WORDS[phase]
  const common = {
    phase,
    word: base.word,
    glyph: base.glyph,
    level: base.level,
    stage: LINK_STAGE[base.level],
    stale: false,
    staleNote: null,
    actionsDisabled: false,
    agent,
    warning: null,
    options: [] as readonly AgentOptionVm[],
    siteOrigin: null,
    troubleshooting: [] as readonly string[],
  }
  switch (phase) {
    case 'searching':
      return { ...common, text: 'Ищем программу-драйвер на этом компьютере…', action: null, agent: null, actionsDisabled: true }
    case 'connected': {
      const known = agent ?? link.selected
      const warning =
        known?.hello.host?.mode === 'console'
          ? 'Агент запущен не как служба: после перезагрузки компьютера он сам не запустится. Установите его установщиком.'
          : null
      return {
        ...common,
        agent: known,
        text: known === null ? 'Программа-драйвер отвечает.' : describeAgent(known),
        action: null,
        warning,
      }
    }
    case 'lost':
      return {
        ...common,
        text: 'Программа-драйвер перестала отвечать. Показаны последние известные данные.',
        action: 'recheck',
        agent: link.selected,
        stale: true,
        staleNote: link.lastSeenAt === null ? null : `на ${formatClock(link.lastSeenAt) ?? '—'}`,
        actionsDisabled: true,
      }
    case 'missing':
      return {
        ...common,
        text: 'Программа-драйвер Kalmykov не найдена на этом компьютере. Она связывает сайт с кассой и терминалом, работает в фоне и стартует вместе с Windows.',
        action: 'download-agent',
        agent: null,
        actionsDisabled: true,
        troubleshooting: MISSING_TROUBLESHOOTING,
      }
    case 'choose':
      return {
        ...common,
        text: 'Найдено несколько программ-драйверов — выберите, с какой работать. До выбора ничего не настраиваем.',
        action: 'agent-pick',
        agent: null,
        actionsDisabled: true,
        options: link.agents.map((item) => ({
          port: item.port,
          version: item.hello.version,
          modeWord: modeWord(item),
          startedAt: startedClock(item),
          selected: link.selected !== null && sameAgent(item, link.selected),
        })),
      }
    case 'outdated': {
      const level = apiLevel(agent)
      return {
        ...common,
        text:
          `${agent === null ? 'Программа-драйвер' : describeAgent(agent)}: версия устарела (уровень ${level}, нужен ${link.minApiLevel}). ` +
          'Обновите программу-драйвер: пока она старая, окно работает только с поиском агента.',
        action: 'update-agent',
      }
    }
    case 'denied':
      return {
        ...common,
        text: 'Программа-драйвер не пускает этот сайт. Добавьте адрес сайта в её настройки (AllowedOrigins).',
        action: 'copy-origin',
        actionsDisabled: true,
        siteOrigin: link.siteOrigin,
      }
  }
}
